import { UserManager, WebStorageStateStore } from 'oidc-client';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';

export class AuthorizeService {
    _callbacks = [];
    _nextSubscriptionId = 0;
    _user = null;
    _isAuthenticated = false;
    _rightsInitialized = false;
    _rights = [];

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    async isAuthenticated() {
        const user = await this.getUser();
        return !!user;
    }

    async getUser() {
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.profile;
    }

    async getAccessToken() {
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.access_token;
    }

    async getRights() {
        console.log("[AUTHSERVICE] GetRights - ENTER");
        if (this._user && this._user.profile && this._rightsInitialized) {
            console.log("[AUTHSERVICE] Rights already initialized, returning from cache");
            return this._rights;
        }

        console.log("[AUTHSERVICE] Rights not read, getting from server...");
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        if (user) {
            console.log("[AUTHSERVICE] User is valid, getting from server...");
            const rights = await this.getUserRights();
            return rights;
        }
        else
            return [];
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(state) {
        await this.ensureUserManagerInitialized();
        try {
            const silentUser = await this.userManager.signinSilent(this.createArguments());
            this.updateState(silentUser);
            try {
                this._rights = await this.getUserRights();
                this._rightsInitialized = true;
            } catch (getRightsError) {
                console.log("Error getting user rights: " + getRightsError);
                this._rights = [];
            }
            return this.success(state);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            console.log("Silent authentication error: ", silentError);

            try {
                if (this._popUpDisabled) {
                    throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
                }

                const popUpUser = await this.userManager.signinPopup(this.createArguments());
                this.updateState(popUpUser);
                try {
                    this._rights = await this.getUserRights();
                    this._rightsInitialized = true;
                } catch (getRightsError) {
                    console.log("Error getting user rights: " + getRightsError);
                    this._rights = [];
                }
                return this.success(state);
            } catch (popUpError) {
                if (popUpError.message === "Popup window closed") {
                    // The user explicitly cancelled the login action by closing an opened popup.
                    return this.error("The user closed the window.");
                } else if (!this._popUpDisabled) {
                    console.log("Popup authentication error: ", popUpError);
                }

                // PopUps might be blocked by the user, fallback to redirect
                try {
                    await this.userManager.signinRedirect(this.createArguments(state));
                    return this.redirect();
                } catch (redirectError) {
                    console.log("Redirect authentication error: ", redirectError);
                    return this.error(redirectError);
                }
            }
        }
    }

    async completeSignIn(url) {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.signinCallback(url);
            this.updateState(user);
            try {
                this._rights = await this.getUserRights();
                this._rightsInitialized = true;
            } catch (getRightsError) {
                console.log("Error getting user rights: " + getRightsError);
                this._rights = [];
            }
            return this.success(user && user.state);
        } catch (error) {
            console.log('There was an error signing in: ', error);
            return this.error('There was an error signing in.');
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state) {
        await this.ensureUserManagerInitialized();
        try {
            if (this._popUpDisabled) {
                throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
            }

            await this.userManager.signoutPopup(this.createArguments());
            this.updateState(undefined);
            this._rights = [];
            this._rightsInitialized = false;

            return this.success(state);
        } catch (popupSignOutError) {
            console.log("Popup signout error: ", popupSignOutError);
            try {
                await this.userManager.signoutRedirect(this.createArguments(state));
                return this.redirect();
            } catch (redirectSignOutError) {
                console.log("Redirect signout error: ", redirectSignOutError);
                return this.error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url) {
        await this.ensureUserManagerInitialized();
        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);
            this._rights = [];
            this._rightsInitialized = false;
            return this.success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return this.error(error);
        }
    }

    async userHasRight(right) {
        const authenticated = await authService.isAuthenticated();

        if (authenticated) {
            if (right) {
                try {
                    const token = await authService.getAccessToken();
                    const authorizeResponse = await fetch('/api/data/userHasRight?drept=' + right, {
                        headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                    });
                    if ((authorizeResponse.ok) && (authorizeResponse.status != 401)) {
                        const authorizeData = await authorizeResponse.json();
                        return authenticated && authorizeData;
                    }
                    else {
                        console.log("Authorization request failed with 401 code");
                        return false;
                    }

                } catch (error) {
                    console.log("Authorization right fetch error:", error);
                    return false;
                }
            }
            else {
                console.log("authorized because no specific right is defined");
                return authenticated;
            }
        }
        else
            return false;
    }

    async getUserRights() {
        console.log("[AUTHSERVICE] GetUserRights - ENTER");

        console.log("[AUTHSERVICE] GetUserRights - Checking if authenticated");
        const authenticated = await authService.isAuthenticated();
        console.log("[AUTHSERVICE] GetUserRights - Authenticated result", authenticated);

        if (authenticated) {
            try {
                console.log("[AUTHSERVICE] GetUserRights - Getting access token");
                const token = await authService.getAccessToken();
                console.log("[AUTHSERVICE] GetUserRights - Got access token, fetching data", token);
                const authorizeResponse = await fetch('/api/data/getUserRights', {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                });
                console.log("[AUTHSERVICE] GetUserRights - Data fetched!", authorizeResponse);
                if ((authorizeResponse.ok) && (authorizeResponse.status != 401)) {
                    console.log("[AUTHSERVICE] GetUserRights - Response valid, parsing result...");
                    const authorizeData = await authorizeResponse.json();
                    console.log("[AUTHSERVICE] GetUserRights - Response valid, result is", authorizeData);
                        return authorizeData;
                    }
                    else {
                        return [];
                    }

            } catch (error) {
                return [];
            }
        }
        else {
            return [];
        }
    }

    updateState(user) {
        this._user = user;
        this._isAuthenticated = !!this._user;
        if (!!this._user) {
            this._rights = [];
            this._rightsInitialized = false;
        }
        this.notifySubscribers();
    }

    subscribe(callback) {
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) => element.subscription === subscriptionId ? { found: true, index } : { found: false })
            .filter(element => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        }

        this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    createArguments(state) {
        return { useReplaceToNavigate: true, data: state };
    }

    error(message) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    async ensureUserManagerInitialized() {
        if (this.userManager !== undefined) {
            return;
        }

        let response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
        if (!response.ok) {
            throw new Error(`Could not load settings for '${ApplicationName}'`);
        }

        let settings = await response.json();
        settings.automaticSilentRenew = true;
        settings.includeIdTokenInSilentRenew = true;
        settings.userStore = new WebStorageStateStore({
            prefix: ApplicationName
        });

        this.userManager = new UserManager(settings);

        this.userManager.events.addUserSignedOut(async () => {
            await this.userManager.removeUser();
            this.updateState(undefined);
            this._rights = [];
            this._rightsInitialized = false;
        });
    }

    static get instance() { return authService }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail'
};
